import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as RGL from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { AppDispatch } from 'store'
import { getHrDashboardData, clearHrDashboard } from 'features/hrDashboard/hr.slice'
import { clearFoodReport } from 'features/food/foodSlice'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { PageWrapper, DashboardCard } from 'styling/GlobalStyles'
import { WidgetHeader, WidgetTitle, WidgetDetailsLink } from './Widgets/widgetParts.styled'
import { HrKpiBand } from './Widgets/HrKpiBand'
import { HrWidgetBody } from './Widgets/HrWidgetBody'
import {
  PageHeader,
  HeaderText,
  PageTitle,
  GridArea,
  WidgetBody,
  PaletteOverlay,
  PaletteDrawer,
  PaletteHeader,
  PaletteTitle,
  PaletteItem,
  PaletteItemInfo,
  PaletteItemTitle,
  PaletteItemDesc,
  PaletteEmpty,
} from './hrLayout.styled'

const ResponsiveGridLayout = RGL.WidthProvider(RGL.Responsive)

const STORAGE_KEY = 'hrDashboardState'

const WIDGET_META = {
  attendance: { title: 'Attendance', desc: 'Present vs absent breakdown', detail: 'Details', to: '/hr/attendance', w: 1, h: 4, minW: 1, minH: 3 },
  department: { title: 'By department', desc: 'Headcount per department', detail: 'View all', to: '', w: 1, h: 4, minW: 1, minH: 3 },
  accommodation: { title: 'Accommodation', desc: 'Bed occupancy across sites', detail: 'Manage', to: '/hr/accommodation', w: 1, h: 4, minW: 1, minH: 2 },
  shifts: { title: 'Shifts', desc: 'Active shifts on site', detail: 'Details', to: '/hr/schedule', w: 1, h: 4, minW: 1, minH: 2 },
  notifications: { title: 'Notifications', desc: 'Recent HR activity', detail: 'View more', to: '', w: 1, h: 4, minW: 1, minH: 3 },
  alerts: { title: 'Alerts & requests', desc: 'Items needing attention', detail: 'View all', to: '', w: 1, h: 4, minW: 1, minH: 3 },
  food: { title: 'Food operations', desc: 'Meal service status', detail: 'Details', to: '/hr/food', w: 1, h: 4, minW: 1, minH: 2 },
  transport: { title: 'Transportation', desc: 'Inbound / outbound demand', detail: 'View all', to: '/hr/transportation', w: 1, h: 4, minW: 1, minH: 2 },
  suggestions: { title: 'AI suggestions', desc: 'Recommended actions', detail: 'View more', to: '', w: 1, h: 4, minW: 1, minH: 3 },
}

const ALL_KEYS = Object.keys(WIDGET_META)

const DEFAULT_PRESENT = ['attendance', 'department', 'accommodation', 'shifts', 'notifications', 'alerts']

const DEFAULT_LAYOUT: RGL.Layout[] = [
  { i: 'attendance', x: 0, y: 0, w: 1, h: 4, minW: 1, minH: 3 },
  { i: 'department', x: 1, y: 0, w: 1, h: 4, minW: 1, minH: 3 },
  { i: 'accommodation', x: 2, y: 0, w: 1, h: 4, minW: 1, minH: 2 },
  { i: 'shifts', x: 0, y: 4, w: 1, h: 4, minW: 1, minH: 2 },
  { i: 'notifications', x: 1, y: 4, w: 1, h: 4, minW: 1, minH: 3 },
  { i: 'alerts', x: 2, y: 4, w: 1, h: 4, minW: 1, minH: 3 },
]

const loadState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed.present) && Array.isArray(parsed.layout) && parsed.present.length) {
        return { present: parsed.present as string[], layout: parsed.layout as RGL.Layout[] }
      }
    }
  } catch {
    return { present: DEFAULT_PRESENT, layout: DEFAULT_LAYOUT }
  }
  return { present: DEFAULT_PRESENT, layout: DEFAULT_LAYOUT }
}

export const HrDashboard = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const [editMode, setEditMode] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [present, setPresent] = useState<string[]>(() => loadState().present)
  const [layout, setLayout] = useState<RGL.Layout[]>(() => loadState().layout)

  useEffect(() => {
    dispatch(getHrDashboardData())
    return () => {
      dispatch(clearHrDashboard())
      dispatch(clearFoodReport())
    }
  }, [])

  const persist = (nextPresent: string[], nextLayout: RGL.Layout[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ present: nextPresent, layout: nextLayout }))
  }

  const handleLayoutChange = (current: RGL.Layout[]) => {
    if (!editMode) return
    setLayout(current)
    persist(present, current)
  }

  const addWidget = (key: string) => {
    if (present.includes(key)) return
    const meta = WIDGET_META[key]
    const nextPresent = [...present, key]
    const nextLayout = [...layout, { i: key, x: 0, y: Infinity, w: meta.w, h: meta.h, minW: meta.minW, minH: meta.minH }]
    setPresent(nextPresent)
    setLayout(nextLayout)
    persist(nextPresent, nextLayout)
  }

  const removeWidget = (key: string) => {
    const nextPresent = present.filter((k) => k !== key)
    const nextLayout = layout.filter((l) => l.i !== key)
    setPresent(nextPresent)
    setLayout(nextLayout)
    persist(nextPresent, nextLayout)
  }

  const available = ALL_KEYS.filter((k) => !present.includes(k))

  const renderBody = (key: string) => <HrWidgetBody type={key} />

  const renderWidget = (key: string) => {
    const meta = WIDGET_META[key]
    return (
      <DashboardCard height='100%' glow={key === 'alerts'}>
        <WidgetHeader>
          <WidgetTitle>{meta.title}</WidgetTitle>
          {editMode ? (
            <Button variant='black' onClick={() => removeWidget(key)} aria-label={'Remove ' + meta.title}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M6 6l12 12M18 6L6 18' />
              </svg>
            </Button>
          ) : (
            <WidgetDetailsLink onClick={() => meta.to && navigate(meta.to)}>{meta.detail} ›</WidgetDetailsLink>
          )}
        </WidgetHeader>
        <WidgetBody>{renderBody(key)}</WidgetBody>
      </DashboardCard>
    )
  }

  return (
    <>
      <NavBar input placeholder='Search staff, sites, documents…' setParams={() => undefined} params={{}} />
      <PageWrapper noFooter>
        <PageHeader>
          <HeaderText>
            <PageTitle>HR Dashboard</PageTitle>
          </HeaderText>
          {editMode && (
            <Button variant='secondary' onClick={() => setPaletteOpen(true)}>
              + Add widget
            </Button>
          )}
          <Button variant='primary' onClick={() => setEditMode((value) => !value)}>
            {editMode ? 'Done' : 'Edit widgets'}
          </Button>
        </PageHeader>

        <HrKpiBand />

        <GridArea $editing={editMode}>
          <ResponsiveGridLayout
            layouts={{ lg: layout }}
            breakpoints={{ lg: 1100, md: 800, sm: 0 }}
            cols={{ lg: 3, md: 2, sm: 1 }}
            rowHeight={40}
            margin={[12, 12]}
            isDraggable={editMode}
            isResizable={editMode}
            resizeHandles={['se', 's', 'e']}
            draggableCancel='button,a'
            onLayoutChange={handleLayoutChange}
            useCSSTransforms
          >
            {present.map((key) => (
              <div key={key}>{renderWidget(key)}</div>
            ))}
          </ResponsiveGridLayout>
        </GridArea>

        {paletteOpen && (
          <PaletteOverlay onClick={() => setPaletteOpen(false)}>
            <PaletteDrawer onClick={(e) => e.stopPropagation()}>
              <PaletteHeader>
                <PaletteTitle>Add widget</PaletteTitle>
                <Button variant='black' onClick={() => setPaletteOpen(false)} aria-label='Close'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M6 6l12 12M18 6L6 18' />
                  </svg>
                </Button>
              </PaletteHeader>
              {available.length === 0 ? (
                <PaletteEmpty>All widgets are on your dashboard.</PaletteEmpty>
              ) : (
                available.map((key) => (
                  <PaletteItem key={key}>
                    <PaletteItemInfo>
                      <PaletteItemTitle>{WIDGET_META[key].title}</PaletteItemTitle>
                      <PaletteItemDesc>{WIDGET_META[key].desc}</PaletteItemDesc>
                    </PaletteItemInfo>
                    <Button variant='secondary' onClick={() => addWidget(key)}>+ Add</Button>
                  </PaletteItem>
                ))
              )}
            </PaletteDrawer>
          </PaletteOverlay>
        )}
      </PageWrapper>
    </>
  )
}
